import { Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { IconButton, Menu, menuClasses, MenuItem, Tooltip, useMenu } from "@kaltura/ds-react-components";
import { User24Icon } from "@kaltura/ds-react-icons";
import { addBaseUrl, getComponentFromPath, translate } from "@kaltura/mediaspace-shared-utils";
import { ButtonClickAnalyticsType, KmsTypeComponent, KmsTypeLinkLink } from "@kaltura/mediaspace-shared-types";
import { CustomComponent, Divider } from "@kaltura/mediaspace-shared-ui";
import styled from "@emotion/styled";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export type UserMenuItem =
    | KmsTypeLinkLink
    | "DIVIDER"
    | KmsTypeComponent
    | {
          // Component to render as the clickable menu item in the dropdown
          menuItemComponent: KmsTypeComponent;
          // Component to launch/open when the menu item is clicked (e.g., modal, full-screen app)
          launchedComponent: KmsTypeComponent;
      };

export type UserMenuProps = {
    className?: string;
    disableScrollLock?: boolean;
    items: UserMenuItem[];
};

const StyledMenuItem = styled(MenuItem)({
    // override eventsplatform css
    ".eventsplatform &": {
        color: "inherit",
        [`&:hover`]: {
            color: "inherit",
        },
    },
    // override v2ui and bootstrap
    [`a&`]: {
        color: "inherit",
        [`&:hover, &:focus`]: {
            color: "inherit",
            textDecoration: "none",
        },
    },
});

const StyledMenu = styled(Menu)(({ theme }) => ({
    [`& .${menuClasses.paper}`]: {
        [theme.breakpoints.down("sm")]: {
            top: "10px",
            maxHeight: "150px",
        },
    },
}));

const renderItem = (
    item: UserMenuItem,
    index: number,
    sendButtonAnalytics: (actionName: string, buttonType: number) => void,
    closeMenu: () => void,
    onActiveComponentSelected: Dispatch<
        SetStateAction<{ component: KmsTypeComponent<any, { open: boolean }>; key: number } | null>
    >
) => {
    if (item === "DIVIDER") {
        return <Divider key={`divider_${index}`} />;
    }

    const handleMenuItemClick = () => {
        if ("attributes" in item || "value" in item) {
            sendButtonAnalytics(
                `Header Menu - ${item.attributes?.["data-analytics-value"] ?? item.value}`,
                ButtonClickAnalyticsType.NAVIGATE
            );
        }

        // Launch the target KMS type component when menu item is clicked
        if ("launchedComponent" in item) {
            closeMenu();
            const selectedComponent = item.launchedComponent as KmsTypeComponent<any, { open: boolean }>;
            // Increment key to force component remount with fresh state
            onActiveComponentSelected((prev) => ({
                component: selectedComponent,
                key: (prev?.key ?? 0) + 1,
            }));
            return;
        }

        onActiveComponentSelected(null);
    };

    // custom menu item component
    if ("component" in item) {
        const { component, props: componentProps } = item;
        const ItemComponent = getComponentFromPath(component);
        return <ItemComponent {...componentProps} key={index} closeMenu={closeMenu} />;
    }

    // Custom menu item that launches another component on click
    // Example: menu button that opens modal or any other KMS type component when clicked
    if ("menuItemComponent" in item) {
        const { menuItemComponent } = item;
        const { component, props: componentProps } = menuItemComponent;
        const ItemComponent = getComponentFromPath(component);
        return <ItemComponent {...componentProps} key={index} onClick={handleMenuItemClick} closeMenu={closeMenu} />;
    }

    return (
        <StyledMenuItem
            key={index}
            component={"a"}
            href={addBaseUrl(item.href)}
            target={(item.attributes?.target as string) ?? "_self"}
            rel={(item.attributes?.rel as string) ?? ""}
            onClick={handleMenuItemClick}
        >
            {item.label}
        </StyledMenuItem>
    );
};

const UserMenuButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.common.white,
    [`&:hover`]: {
        backgroundColor: "initial",
    },
}));

/**
 * User Menu component
 */
const UserMenu: FunctionComponent<UserMenuProps> = ({ className = "", disableScrollLock = false, items }) => {
    const sendButtonAnalytics = useButtonAnalytics();

    const { buttonProps, menuProps, close } = useMenu({
        menuId: "userMenuToggleMenu",
    });

    // Tracks the currently launched component; key increments to force fresh render
    const [activeComponent, setActiveComponent] = useState<{
        component: KmsTypeComponent<any, { open: boolean }>;
        key: number;
    } | null>(null);

    return (
        <div className={`kms-ds-user-menu ${className}`}>
            <div id="userMenuToggle">
                <Tooltip title={translate("user menu")} placement={"bottom-start"}>
                    <UserMenuButton
                        {...buttonProps}
                        color="translucent"
                        id={"userMenuToggleBtn"}
                        aria-label={translate("user menu")}
                    >
                        <User24Icon />
                    </UserMenuButton>
                </Tooltip>
                <StyledMenu
                    {...menuProps}
                    disableScrollLock={disableScrollLock}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    slotProps={{
                        list: {
                            "aria-labelledby": "userMenuToggleBtn",
                        },
                    }}
                >
                    {items.map((item, index) =>
                        renderItem(item, index, sendButtonAnalytics, close, setActiveComponent)
                    )}
                </StyledMenu>
                {/* Render the launched component outside the menu */}
                {activeComponent && (
                    <CustomComponent component={activeComponent.component} key={activeComponent.key} open={true} />
                )}
            </div>
        </div>
    );
};

export default UserMenu;
